import type { FieldMetadata, ObjectMetadata, RecordRow } from '../../data/client';
import { RecordDetails } from './RecordDetails';
import { RecordMetaLine } from './RecordMetaCard';
import { RelatedSections } from './related/RelatedSections';
import { TaskSection } from './TaskSection';

/**
 * THE RECORD'S OVERVIEW: everything about this record that is not its history, in one
 * scrolling content pane (wave 3 round 2, R-B2).
 *
 * WHY THIS COMPONENT EXISTS. Until this round the same four parts were assembled twice - in
 * the record page's inspector aside and again in the list's detail pane - and the reading
 * order `spec/screens/record.md` section 2 item 6 gives lived in neither of them as a single
 * statement. It is one statement now, and the three surfaces that show a record's Overview
 * take it from here: the record page's Overview segment, the phone's Details segment, and the
 * list's detail pane once the list slice wires it.
 *
 * THE ORDER IS THE SPEC'S, and each step of it is a decision somebody already argued:
 * the field sections first, because the values are what somebody opened the record for;
 * identity and merges after them, because "is this the right record" and "why does it hold
 * that" are questions a reader asks AFTER reading the values (round 1); the tasks, because
 * `notes-tasks-comments.md` 12.1 puts state beside the values it is about rather than in the
 * history; then what points at this record; then the created and updated line as a footnote.
 * Files are not a section of their own - a files field is a field, and it spans the grid where
 * the grid has more than one column (`RecordFieldRow`, `.record-row-wide`).
 *
 * IT TAKES NO LAYOUT PROP. Every part inside it counts the box it is drawn in rather than the
 * window, so the pane at 611px gets two columns from the same rule that gives the page three
 * (`.record-grid` in index.css). A caller decides how wide this is; nothing here asks.
 */
export interface RecordOverviewProps {
  readonly object: ObjectMetadata;
  readonly fields: readonly FieldMetadata[];
  readonly record: RecordRow;
  readonly locale?: string | undefined;
  /**
   * The fields the band above already shows (BR-RECORD, register A6-13: header facts "never
   * repeat below"). Passed by a surface that draws the band over this Overview; the phone's
   * Details segment, which has no band, passes nothing and keeps every row.
   */
  readonly hiddenFieldIds?: readonly string[] | undefined;
}

export function RecordOverview({
  object,
  fields,
  record,
  locale,
  hiddenFieldIds,
}: RecordOverviewProps) {
  /** D4: create and link are HIDDEN on an archived parent - a live child pointing at a
      tombstone is a relationship the model cannot describe. D21 makes every row read-only,
      which `RecordDetails` reads off the same `deletedAt`. */
  const archived = record.deletedAt !== null;

  return (
    <>
      <RecordDetails
        object={object}
        fields={fields}
        record={record}
        hiddenFieldIds={hiddenFieldIds}
      />
      <TaskSection objectId={object.id} recordId={record.id} locale={locale} />
      <RelatedSections object={object} recordId={record.id} readOnly={archived} />
      <RecordMetaLine record={record} locale={locale} />
    </>
  );
}
